import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'

import { TrialStatus } from '@/reducers/auth/userReducer'
import { getFetchError } from '@/reducers/fetch'
import { PlanTier } from '@/actions/settings/billingActions'
import {
  getCurrentUser,
  getFreeTrialStatus,
  selectFreeTrialLengthDays,
} from '@/selectors/user.selectors'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { useSignupPageUpdate } from '@/services/onboardingService'
import {
  useFetchResponseWithUtils,
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsView } from '@/features/amplitude'

import {
  FREE_TRIAL_PROMO,
  SIGNUP_PATHS,
  fetchFreeTrialLength,
} from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { useSignupCheckout } from '../checkout/useSignupCheckout'
import {
  isChooserBypassCohort,
  useResolveChooserShown,
} from '../plans/chooserVisibility'
import { NO_MATCHING_PLANS_MESSAGE } from '../plans/noPlansMessage'
import {
  fetchSignupPlans,
  FETCH_SIGNUP_PLANS_KEY,
} from '../plans/signupPlans.actions'
import {
  effectiveCadence,
  orderedCards,
  selectPaymentTier,
  selectionFor,
} from '../plans/signupPlans'
import type { SignupCadence } from '../plans/signupPlans.types'
import { TextColors, TypographyStyles } from '@/styles'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'
import {
  Alert,
  Button,
  Card,
  CardColors,
  Checkbox,
  Separator,
  Text,
  Loader,
} from '@/ui'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import { CatalogStripeProductCard } from './CatalogStripeProductCard'
import {
  CHOSEN_PLAN_NOT_OFFERED_MESSAGE,
  NO_PLAN_CHOSEN_MESSAGE,
} from './catalogHelpers'
import { selectCanOptIntoPreviousTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'

export const CatalogPayment = ({ isUpsell }: { isUpsell?: boolean }) => {
  const navigate = useNavigateWithPersistParams()
  const dispatch = useAppDispatch()
  const [annualCommitmentConfirmation, setAnnualCommitmentConfirmation] =
    useState(false)
  const user = useReselector(getCurrentUser)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)
  const [searchParams] = useSearchParams()
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )

  // Lite is never offered to the bypass cohort (S corps, C corps and group practices).
  const bypassCohort = isChooserBypassCohort(user?.financialProfile)
  const resolveChooserShown = useResolveChooserShown()

  // This instance's own request state: the shared fetch key can already read resolved from
  // the chooser while this page's result is still empty.
  const { result: plans, loading: fetchingPlans } =
    useFetchResponseWithUtils(fetchSignupPlans)
  const cards = useMemo(
    () => orderedCards(plans ?? [], { excludeLite: bypassCohort }),
    [plans, bypassCohort]
  )
  const [chosenCadence, setCadence] = useState<SignupCadence | null>(null)
  const storedTier = localStorage.getItem('planTier')
  // The upsell has no chooser to return to, so a stored plan the list dropped falls back to the default.
  const selectedTier = selectPaymentTier(storedTier, cards, {
    fallBackWhenStale: Boolean(isUpsell),
  })
  const selectedPlan = cards.find((card) => card.tier === selectedTier)
  const cadence = effectiveCadence(selectedPlan, chosenCadence)
  const selection = selectionFor(selectedPlan, cadence)
  const selectedLitePlan = selectedTier === PlanTier.lite

  const { submit, loading, checkoutError, useTwoCheckoutFlow } =
    useSignupCheckout({
      selection,
      successUrl: SIGNUP_PATHS.success,
      screenName: SIGNUP_PATHS.payment,
      isUpsell,
    })

  /* Free-trial related */
  // e.g. active, inactive, unredeemed
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)

  // Based on user's current & past activity, should we enable the trial
  const freeTrialSearchParamValue = searchParams.get('trial')
  const freeTrialEnabled = useFreeTrialEnabled(freeTrialSearchParamValue)
  useSignupPageUpdate(SIGNUP_PATHS.payment, isUpsell)

  const pageView = useAnalyticsView()

  useEffect(() => {
    if (isUpsell) {
      pageView('upsell flow choose plan')
    } else {
      pageView('sign up choose plan')
    }
  }, [pageView, isUpsell])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useEffect(() => {
    if (checkoutError) {
      scrollToRef()
    }
  }, [checkoutError, scrollToRef])

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
  }, [dispatch])

  const fetchingPlansError = useReselector(
    getFetchError,
    FETCH_SIGNUP_PLANS_KEY
  )

  const handleBackButton = async () => {
    const backToTaxYear =
      canOptIntoPreviousYearTaxes &&
      freeTrialSearchParamValue !== FREE_TRIAL_PROMO &&
      !selectedLitePlan
    if (backToTaxYear) {
      navigate(SIGNUP_PATHS.previousYearTax)
      return
    }
    // Without the tax-year step, Back returns to the chooser only if the visitor saw it.
    navigate(
      (await resolveChooserShown())
        ? SIGNUP_PATHS.choosePlan
        : SIGNUP_PATHS.aboutYourPractice
    )
  }

  /** Calculate Free Trial Days based on referral date */
  const freeTrialDays = fetchFreeTrialLength(freeTrialSearchParamValue)
  // An ended trial has no `?trial=` param to read, so take its length from the
  // membership instead, and omit the count entirely if the code is unknown.
  const endedTrialLength = useReselector(selectFreeTrialLengthDays)
  const trialEndDate = moment()
    .add(freeTrialDays, 'days')
    .format(DATE_FORMATS.DISPLAY_SIMPLE)

  const planSubText = useMemo(() => {
    if (freeTrialEnabled && !isUpsell) {
      return `Try Heard for the first ${freeTrialDays} days before getting billed. You won't be charged until your trial ends on ${trialEndDate}. Cancel anytime.`
    } else {
      if (hasAnnualCommitment) {
        return "Based on your responses, we've selected the best plan for your practice. Please note that all plans require a 12-month commitment, regardless of monthly or annual payment choice."
      } else {
        return 'Based on your answers, the plan below fits your practice.'
      }
    }
  }, [
    freeTrialEnabled,
    freeTrialDays,
    isUpsell,
    hasAnnualCommitment,
    trialEndDate,
  ])

  const headerTitle = isUpsell
    ? 'Ready to unlock the full experience?'
    : 'Choose to pay annually or monthly, whatever meets your needs.'
  const continueDisabled =
    loading ||
    !selection ||
    (hasAnnualCommitment && !annualCommitmentConfirmation)

  /* No plans found - in the event products aren't properly configured on Stripe.com */
  const noPlansAvailable =
    !fetchingPlans && !fetchingPlansError && cards.length === 0
  const chosenPlanNotOffered =
    !isUpsell &&
    !fetchingPlans &&
    !fetchingPlansError &&
    cards.length > 0 &&
    !selectedPlan

  const content = (
    <div className="flex flex-col gap-4">
      <span ref={scrollRef} />

      <div className="w-full">
        <Text variant={TypographyStyles.DISPLAY_MD}>{headerTitle}</Text>

        {isUpsell && (
          <Text variant={TypographyStyles.BODY_LG}>
            Join thousands of {replacementText} entrepreneurs who trust Heard to
            handle their finances. By purchasing Heard, you will gain full
            access to bookkeeping services, tax support, and financial tools
            designed specifically for your practice.
          </Text>
        )}
      </div>

      {userFreeTrialStatus === TrialStatus.inactive && (
        <Card color={CardColors.WARNING}>
          <Text>
            Your {endedTrialLength ? `${endedTrialLength}-day ` : ''}free trial
            has ended. Please choose a plan and complete payment to access your
            heard account.
          </Text>
        </Card>
      )}

      {userFreeTrialStatus !== TrialStatus.inactive && !isUpsell && (
        <Text
          className="w-full min-h-fit !mb-8"
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
        >
          {planSubText}
        </Text>
      )}

      {checkoutError && <Alert variant="error">{checkoutError}</Alert>}

      <div className="w-full">
        <Loader active={fetchingPlans} />

        {fetchingPlansError && (
          <Card color={CardColors.LEVEL_0}>
            <Text variant={TypographyStyles.H2}>Missing information</Text>
            <Text>{fetchingPlansError.message}</Text>
          </Card>
        )}

        {!fetchingPlans && selectedPlan && (
          <CatalogStripeProductCard
            plan={selectedPlan}
            cadence={cadence}
            onCadenceChange={setCadence}
            freeTrialEnabled={freeTrialEnabled}
            freeTrialSearchParamValue={freeTrialSearchParamValue}
            useTwoCheckoutFlow={useTwoCheckoutFlow}
          />
        )}

        {noPlansAvailable && <Text>{NO_MATCHING_PLANS_MESSAGE}</Text>}

        {chosenPlanNotOffered && (
          <Alert
            variant="warning"
            ctaText="Choose a plan"
            ctaOnClick={() => navigate(SIGNUP_PATHS.choosePlan)}
          >
            {storedTier
              ? CHOSEN_PLAN_NOT_OFFERED_MESSAGE
              : NO_PLAN_CHOSEN_MESSAGE}
          </Alert>
        )}
      </div>

      {hasAnnualCommitment && (
        <Card color={CardColors.LEVEL_0}>
          <Checkbox
            label="By checking this box, I understand I'm committing to a 12-month membership with Heard, payable either upfront (Annual Payment Plan) or monthly (Monthly Payment Plan)."
            checked={annualCommitmentConfirmation}
            onCheckedChange={(checked: boolean) =>
              setAnnualCommitmentConfirmation(checked)
            }
          />
        </Card>
      )}
    </div>
  )

  if (isUpsell) {
    return (
      <div className="container mx-auto pt-16 mb-4">
        <div className="flex flex-col gap-4">
          {content}

          <Separator className="my-6" />

          <div className="flex justify-end">
            {selectedPlan && (
              <Button
                onClick={submit}
                disabled={continueDisabled}
                loading={loading}
              >
                Purchase
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onBack={handleBackButton}
          onContinue={submit}
          continueDisabled={continueDisabled}
          continueLoading={loading}
          continueId="btn-signup-payment"
        />
      }
    >
      {content}
    </SignupPageLayout>
  )
}
